/**
 * Form Crypto Utilities
 *
 * Provides secure AES-GCM encryption for form state persistence.
 * Uses the native Web Crypto API with session-scoped keys.
 */

const CRYPTO_KEY_STORAGE_KEY = "__form_crypto_key__"
const FORM_PERSIST_PREFIX = "__form_persist_"

/**
 * Convert ArrayBuffer to Base64 string
 */
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let binary = ""
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return btoa(binary)
}

/**
 * Convert Base64 string to ArrayBuffer
 */
function base64ToArrayBuffer(base64: string): ArrayBuffer {
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes.buffer
}

/**
 * Get or create a session-scoped AES-GCM encryption key.
 * The key is stored in sessionStorage and persists for the browser session.
 */
export async function getOrCreateSessionKey(): Promise<CryptoKey> {
  // Check if we have an existing key
  const storedKey = sessionStorage.getItem(CRYPTO_KEY_STORAGE_KEY)

  if (storedKey) {
    try {
      const keyData = base64ToArrayBuffer(storedKey)
      return await crypto.subtle.importKey("raw", keyData, { name: "AES-GCM", length: 256 }, true, [
        "encrypt",
        "decrypt",
      ])
    } catch (error) {
      console.error("[formCrypto] Failed to import stored key, generating new one:", error)
      sessionStorage.removeItem(CRYPTO_KEY_STORAGE_KEY)
    }
  }

  // Generate a new 256-bit AES-GCM key
  const key = await crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, true, [
    "encrypt",
    "decrypt",
  ])

  // Export and store the key
  const exportedKey = await crypto.subtle.exportKey("raw", key)
  sessionStorage.setItem(CRYPTO_KEY_STORAGE_KEY, arrayBufferToBase64(exportedKey))

  return key
}

/**
 * Encrypt form data using AES-GCM.
 * Returns a Base64-encoded string containing the IV and ciphertext.
 */
export async function encryptFormData(data: string): Promise<string> {
  const key = await getOrCreateSessionKey()

  // Generate a random 12-byte IV (recommended for AES-GCM)
  const iv = crypto.getRandomValues(new Uint8Array(12))

  // Encode the data as UTF-8
  const encoder = new TextEncoder()
  const encodedData = encoder.encode(data)

  // Encrypt the data
  const ciphertext = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, encodedData)

  // Combine IV and ciphertext: [12 bytes IV][ciphertext]
  const combined = new Uint8Array(iv.length + ciphertext.byteLength)
  combined.set(iv, 0)
  combined.set(new Uint8Array(ciphertext), iv.length)

  return arrayBufferToBase64(combined.buffer)
}

/**
 * Decrypt form data encrypted with encryptFormData.
 * Returns the original plaintext string.
 */
export async function decryptFormData(encrypted: string): Promise<string> {
  const key = await getOrCreateSessionKey()

  // Decode the Base64 string
  const combined = new Uint8Array(base64ToArrayBuffer(encrypted))

  // Extract IV (first 12 bytes) and ciphertext (rest)
  const iv = combined.slice(0, 12)
  const ciphertext = combined.slice(12)

  // Decrypt the data
  const decrypted = await crypto.subtle.decrypt({ name: "AES-GCM", iv }, key, ciphertext)

  // Decode the UTF-8 plaintext
  const decoder = new TextDecoder()
  return decoder.decode(decrypted)
}

/**
 * Get the storage key for a specific form.
 */
export function getFormStorageKey(formKey: string): string {
  return `${FORM_PERSIST_PREFIX}${formKey}__`
}

/**
 * Clear all persisted form data and the encryption key.
 */
export function clearAllFormPersistence(): void {
  // Find and remove all form persistence keys
  const keysToRemove: string[] = []
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i)
    if (key && (key.startsWith(FORM_PERSIST_PREFIX) || key === CRYPTO_KEY_STORAGE_KEY)) {
      keysToRemove.push(key)
    }
  }
  keysToRemove.forEach((key) => sessionStorage.removeItem(key))
}

/**
 * Clear persisted data for a specific form.
 */
export function clearFormPersistence(formKey: string): void {
  sessionStorage.removeItem(getFormStorageKey(formKey))
}

/**
 * Save encrypted form state to sessionStorage.
 */
export async function saveEncryptedFormState<T>(formKey: string, state: T): Promise<void> {
  try {
    const json = JSON.stringify(state)
    const encrypted = await encryptFormData(json)
    sessionStorage.setItem(getFormStorageKey(formKey), encrypted)
  } catch (error) {
    console.error(`[formCrypto] Failed to save encrypted form state for ${formKey}:`, error)
    throw error
  }
}

/**
 * Load and decrypt form state from sessionStorage.
 * Returns null if no data exists or decryption fails.
 */
export async function loadEncryptedFormState<T>(formKey: string): Promise<T | null> {
  try {
    const encrypted = sessionStorage.getItem(getFormStorageKey(formKey))
    if (!encrypted) {
      return null
    }

    const json = await decryptFormData(encrypted)
    return JSON.parse(json) as T
  } catch (error) {
    console.error(`[formCrypto] Failed to load encrypted form state for ${formKey}:`, error)
    // Clear corrupted data
    clearFormPersistence(formKey)
    return null
  }
}
